import React, { useState, useEffect } from 'react';
import './MobileGravityLayout.css';
import MobileGravityInfo from './MobileGravityInfo';
import { useAuth } from './AuthContext';
import { csrfFetch } from '../utils/csrf';

// API base URL with intelligent detection for mobile testing - matches AuthContext logic
const getApiBaseUrl = () => {
  // Production: use environment variable
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://knotcord.com';
  }
  
  // Development: intelligent detection
  const hostname = window.location.hostname;
  
  // Localhost or LAN IP: always use relative URL so setupProxy.js handles it
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const baseUrl = `${API_BASE_URL}/zoulang/gravity/`;

// API endpoints configured for mobile access

// Helper functions
const isWeekday = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const getWeekdayDates = (dates) => {
  return dates.filter(isWeekday);
};

// Constants
const NUM_CONNECTIONS = 15; // Fixed at 15 connections for mobile
const DEFAULT_SYMBOLS = ['AAPL', 'GOOGL', 'AMZN', 'NVDA', 'MSTR', 'PLTR'];

const MobileGravityLayout = () => {
  const { user, isAuthenticated, loading } = useAuth();

  // Single network state (no comparison mode)
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [network, setNetwork] = useState(null);
  const [universeMetadata, setUniverseMetadata] = useState(null);

  // UI state
  const [showNetwork, setShowNetwork] = useState(false);

  // Symbol suggestions
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [symbolError, setSymbolError] = useState('');
  const [dateError, setDateError] = useState('');

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setError(null);
    setSymbolError('');
    setDateError('');
  };

  const handleSymbolChange = (value) => {
    const upperValue = value.toUpperCase();
    setSelectedSymbol(upperValue);
    setSymbolError('');
    
    // Generate suggestions
    if (upperValue.length > 0) {
      const suggestions = availableSymbols
        .filter(symbol => symbol.startsWith(upperValue))
        .slice(0, 8); // Limit to 8 suggestions
      setSymbolSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0 && suggestions[0] !== upperValue);
    } else {
      setSymbolSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (symbol) => {
    setSelectedSymbol(symbol);
    setShowSuggestions(false);
    setSymbolSuggestions([]);
    setSymbolError('');
  };


  // Custom Roller Date Picker Component
  const RollerDatePicker = ({ selectedDate, onChange, availableDates }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempYear, setTempYear] = useState(selectedDate ? selectedDate.getFullYear() : new Date().getFullYear());
    const [tempMonth, setTempMonth] = useState(selectedDate ? selectedDate.getMonth() : new Date().getMonth());
    const [tempDay, setTempDay] = useState(selectedDate ? selectedDate.getDate() : new Date().getDate());
    
    // Generate years from available dates
    const availableYears = availableDates.length > 0 ?
      [...new Set(availableDates.map(date => date.getFullYear()))].sort((a, b) => b - a) :
      [2025]; // fallback

    // Always show all 12 months
    const availableMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    // Calculate all valid days for the selected month and year (accounting for leap years)
    const getDaysInMonth = (year, month) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const availableDays = Array.from(
      { length: getDaysInMonth(tempYear, tempMonth) },
      (_, i) => i + 1
    );

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Update temp values when selectedDate changes or picker opens
    useEffect(() => {
      if (selectedDate) {
        setTempYear(selectedDate.getFullYear());
        setTempMonth(selectedDate.getMonth());
        setTempDay(selectedDate.getDate());
      }
    }, [selectedDate, isOpen]);

    // Adjust day if it exceeds the days in the selected month (e.g., Jan 31 -> Feb 28)
    useEffect(() => {
      const daysInMonth = getDaysInMonth(tempYear, tempMonth);
      if (tempDay > daysInMonth) {
        setTempDay(daysInMonth);
      }
    }, [tempYear, tempMonth, tempDay]);
    
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (isOpen && !event.target.closest('.roller-date-picker')) {
          setIsOpen(false);
        }
      };
      
      if (isOpen) {
        // Use timeout to prevent interference with roller item clicks
        const timeoutId = setTimeout(() => {
          document.addEventListener('click', handleClickOutside);
        }, 0);
        
        return () => {
          clearTimeout(timeoutId);
          document.removeEventListener('click', handleClickOutside);
        };
      }
    }, [isOpen]);
    
    const formatDisplayDate = (date) => {
      if (!date) return 'Select a date';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const handleConfirm = () => {
      const newDate = new Date(tempYear, tempMonth, tempDay);
      
      // If no available dates loaded yet, allow any date (will be validated by backend)
      if (availableDates.length === 0) {
        onChange(newDate);
        setIsOpen(false);
        setDateError('');
        return;
      }
      
      // Validate that the selected date is in the availableDates array
      const isDateAvailable = availableDates.some(availableDate => 
        availableDate.getFullYear() === newDate.getFullYear() &&
        availableDate.getMonth() === newDate.getMonth() &&
        availableDate.getDate() === newDate.getDate()
      );
      
      if (isDateAvailable) {
        onChange(newDate);
        setIsOpen(false);
        setDateError('');
      } else {
        setDateError('Selected date is not available. Please choose from available dates.');
      }
    };

    const RollerColumn = ({ items, selected, onSelect, renderItem, label }) => (
      <div className="roller-column">
        <div className="roller-label">{label}</div>
        <div className="roller-wheel">
          {items.map((item, index) => (
            <div
              key={item}
              className={`roller-item ${item === selected ? 'selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
              }}
            >
              {renderItem ? renderItem(item) : item}
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <div className="roller-date-picker">
        <div className="roller-input" onClick={() => setIsOpen(!isOpen)}>
          <span>{formatDisplayDate(selectedDate)}</span>
          <span className="roller-arrow">{isOpen ? '▲' : '▼'}</span>
        </div>
        
        {isOpen && (
          <div className="roller-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="roller-container">
              <RollerColumn 
                items={availableYears}
                selected={tempYear}
                onSelect={setTempYear}
                label="Year"
              />
              <RollerColumn 
                items={availableMonths}
                selected={tempMonth}
                onSelect={setTempMonth}
                renderItem={(month) => monthNames[month]}
                label="Month"
              />
              <RollerColumn 
                items={availableDays}
                selected={tempDay}
                onSelect={setTempDay}
                label="Day"
              />
            </div>
            <div className="roller-actions">
              <button className="roller-btn cancel" onClick={() => setIsOpen(false)}>
                Cancel
              </button>
              <button className="roller-btn confirm" onClick={handleConfirm}>
                Select
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fetch available dates and universe metadata
  useEffect(() => {
    // Authentication check for mobile users
    
    if (!isAuthenticated) {
      // User authentication required for data access
      return;
    }
    
    const fetchInitialData = async () => {
      try {
        // Fetching available dates for mobile interface (optimized: only recent 90 days)
        const datesResponse = await csrfFetch(`${baseUrl}dates/?limit=90`, {
          method: 'GET'
        });

        // Processing dates response

        if (datesResponse.ok) {
          const datesData = await datesResponse.json();
          // Dates data processed for mobile interface

          if (datesData.dates && Array.isArray(datesData.dates)) {
            const parsedDates = datesData.dates.map(dateStr => {
              const [year, month, day] = dateStr.split('-').map(Number);
              return new Date(year, month - 1, day);
            });

            const validDates = parsedDates.filter(date => date instanceof Date && !isNaN(date));
            const weekdayDates = getWeekdayDates(validDates);

            // Weekday dates filtered for mobile display
            setAvailableDates(weekdayDates);

            if (weekdayDates.length > 0) {
              setSelectedDate(weekdayDates[0]);
            }
          } else {
            console.error('Invalid dates data structure:', datesData);
            setError('Invalid dates data received');
          }
        } else {
          const errorText = await datesResponse.text();
          console.error('Dates fetch failed:', datesResponse.status, errorText);
          setError(`Failed to fetch dates: ${datesResponse.status}`);
        }
      } catch (err) {
        console.error('Fetch error details:', err);
        setError(`Failed to fetch initial data: ${err.message}`);
      }
    };
    
    const fetchMetadata = async () => {
      try {
        // Fetching metadata for mobile interface
        const response = await csrfFetch(`${baseUrl}metadata/`, {
          method: 'GET'
        });
        
        // Processing metadata response
        
        if (response.ok) {
          const data = await response.json();
          // Metadata processed for mobile interface
          setUniverseMetadata(data);

          // Extract available symbols if they exist in metadata
          if (data.available_symbols && Array.isArray(data.available_symbols)) {
            setAvailableSymbols(data.available_symbols);
          }
        } else {
          const errorText = await response.text();
          console.error('Metadata fetch failed:', response.status, errorText);
        }
      } catch (err) {
        console.error('Failed to fetch universe metadata', err);
      }
    };
    
    // Initialize available symbols with comprehensive list
    // Backend doesn't have a /symbols/ endpoint yet, so we use a curated list
    setAvailableSymbols([
      ...DEFAULT_SYMBOLS,
      'MSFT', 'META', 'NFLX', 'GOOG', 'BABA', 'UBER', 'LYFT', 'SNAP', 'TWTR', 'SQ',
      'PYPL', 'ADBE', 'CRM', 'ORCL', 'INTC', 'AMD', 'QCOM', 'AVGO', 'MU', 'LRCX',
      'KLAC', 'AMAT', 'ASML', 'CSCO', 'WORK', 'ZM', 'DOCU', 'OKTA', 'CRWD', 'ZS'
    ]);

    fetchInitialData();
    fetchMetadata();
  }, [isAuthenticated]);

  // Reset everything
  const handleReset = () => {
    setNetwork(null);
    setSelectedSymbol('');
    setError(null);
    setSymbolError('');
    setShowSuggestions(false);
    setSymbolSuggestions([]);
    setShowNetwork(false);
    
    if (availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  };

  // Generate network with fixed connections
  const handleGenerate = async () => {
    if (!selectedDate || !selectedSymbol) {
      setError("Please select both a date and symbol");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSymbolError('');
    setNetwork(null);
    
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      
      // Use fixed 12 connections for mobile
      const fullUrl = `${baseUrl}network/?date1=${formattedDate}&symbol1=${selectedSymbol}&connections1=${NUM_CONNECTIONS}`;
      const response = await csrfFetch(fullUrl, {
        method: 'GET'
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication required. Please log in to access gravity data.');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch data: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.mode === 'single') {
        setNetwork(data.network);
        setShowNetwork(true);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while generating the network');
    } finally {
      setIsLoading(false);
    }
  };

  // Check authentication before rendering - wait for loading to complete
  if (loading) {
    // Still checking auth status - show loading indicator
    return (
      <div className="mobile-layout-container">
        <div className="mobile-loading-screen">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to auth page with gravity intent so user can log in
    window.location.replace('/auth?source=mobile-gravity&intent=gravity');
    return null;
  }

  return (
    <div className="mobile-layout-container">
      {/* Universal Back Button - Always present, changes behavior based on state */}
      <button
        onClick={showNetwork ? () => setShowNetwork(false) : () => window.location.href = '/mobile-home.html'}
        className="mobile-gravity-back-button"
      >
        ←
      </button>

      {/* Control Panel - Always visible at top */}
      {!showNetwork && (
        <div className="mobile-control-panel">
          <div className="mobile-control-content">
            
            <h2 className="mobile-title">Gravity Single Network</h2>
            
            <div className="mobile-control-group">
              <div className="mobile-label-with-info">
                <label>Date</label>
                {universeMetadata && universeMetadata.date_range && (
                  <span className="mobile-date-range">
                    {universeMetadata.date_range.start} to {universeMetadata.date_range.end}
                  </span>
                )}
              </div>
              <RollerDatePicker
                selectedDate={selectedDate}
                onChange={handleDateChange}
                availableDates={availableDates}
              />
              
              {dateError && (
                <div className="mobile-date-error">
                  {dateError}
                </div>
              )}
            </div>
            
            <div className="mobile-control-group">
              <label>Central Symbol</label>
              <div className="mobile-symbol-input-container">
                <input
                  type="text"
                  value={selectedSymbol}
                  onChange={(e) => handleSymbolChange(e.target.value)}
                  onFocus={() => selectedSymbol.length > 0 && setShowSuggestions(symbolSuggestions.length > 0)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="mobile-symbol-input"
                  placeholder="Enter symbol"
                />
                
                {showSuggestions && (
                  <div className="mobile-symbol-suggestions">
                    {symbolSuggestions.map(symbol => (
                      <div
                        key={symbol}
                        className="mobile-suggestion-item"
                        onClick={() => selectSuggestion(symbol)}
                      >
                        {symbol}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {symbolError && (
                <div className="mobile-symbol-error">
                  {symbolError}
                </div>
              )}
              
              <div className="mobile-default-symbols">
                {DEFAULT_SYMBOLS.map(symbol => (
                  <button
                    key={symbol}
                    onClick={() => selectSuggestion(symbol)}
                    className={`mobile-symbol-button ${selectedSymbol === symbol ? 'selected' : ''}`}
                  >
                    {symbol}
                  </button>
                ))}
              </div>
            </div>

            <div className="mobile-connections-info">
              <p>{NUM_CONNECTIONS} connected nodes will show in the single-network view. Visit knotcord.com through desktop computer for more options.</p>
            </div>

            <div className="mobile-action-buttons">
              <button
                onClick={handleGenerate}
                disabled={!selectedDate || !selectedSymbol || isLoading}
                className="mobile-generate-button"
              >
                {isLoading ? 'Loading...' : 'Generate'}
              </button>
              
              <button
                onClick={handleReset}
                className="mobile-reset-button"
              >
                Reset
              </button>
            </div>

            {error && (
              <div className="mobile-error-message">
                {error}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Network Summary */}
      {showNetwork && network && (
        <div className="mobile-network-container">
          <div className="mobile-network-summary-top">
            <span>{selectedSymbol} • {network.current_date} • {NUM_CONNECTIONS} nodes</span>
          </div>
        </div>
      )}

      {/* Info Panel - Shows below network via scroll */}
      {showNetwork && network && (
        <MobileGravityInfo
          network={network}
          numConnections={NUM_CONNECTIONS}
        />
      )}
    </div>
  );
};

export default MobileGravityLayout;